import { tr } from '@/lib/i18n-core';
import { useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { NotebookPen } from 'lucide-react';
import { post } from '@/lib/api';
import { describeError } from '@/lib/auth';
import { humanize } from '@/lib/utils';
import { FormActions, FormScreen, FormSection } from '@/components/ui/form-screen';
import { Field, Textarea } from '@/components/ui/primitives';
import { ChoiceButtons } from '@/components/ui/choice-buttons';
import { useToast } from '@/components/ui/toast';
import { safeReturnTo } from '../opd/BookAppointmentPage';

type NoteKind = 'progress' | 'handover' | 'care_plan' | 'incident';
type Shift = 'morning' | 'evening' | 'night' | '';

export function IpdNotePage() {
  const { admissionId = '' } = useParams<{ admissionId: string }>();
  const [params] = useSearchParams();
  const kind = params.get('kind') === 'nursing' ? 'nursing' : 'progress';
  const returnTo = safeReturnTo(params.get('returnTo'), `/ipd/admissions/${admissionId}?tab=notes`);
  const toast = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [subjective, setSubjective] = useState('');
  const [objective, setObjective] = useState('');
  const [assessment, setAssessment] = useState('');
  const [plan, setPlan] = useState('');
  const [noteKind, setNoteKind] = useState<NoteKind>('progress');
  const [shift, setShift] = useState<Shift>('');
  const [situation, setSituation] = useState('');
  const [recommendation, setRecommendation] = useState('');
  const [body, setBody] = useState('');

  const save = useMutation({
    mutationFn: () =>
      kind === 'progress'
        ? post('/ipd/progress-notes', {
            admissionId,
            subjective: subjective || undefined,
            objective: objective || undefined,
            assessment: assessment || undefined,
            plan: plan || undefined,
          })
        : post('/ipd/nursing-notes', {
            admissionId,
            kind: noteKind,
            shift: shift || undefined,
            situation: situation || undefined,
            recommendation: recommendation || undefined,
            body: body || undefined,
          }),
    onSuccess: () => {
      toast.success(kind === 'progress' ? tr('ipd', 'progress_note_added') : tr('ipd', 'nursing_note_added'));
      void queryClient.invalidateQueries({ queryKey: ['admission', admissionId] });
      navigate(returnTo, { replace: true });
    },
    onError: (error) => toast.error(tr('ipd', 'could_not_save_the_note'), describeError(error)),
  });

  const ready =
    kind === 'progress'
      ? [subjective, objective, assessment, plan].some((v) => v.trim())
      : [situation, recommendation, body].some((v) => v.trim());
  const dirty =
    !save.isSuccess &&
    [subjective, objective, assessment, plan, situation, recommendation, body].some((v) => v !== '');

  return (
    <FormScreen
      title={kind === 'progress' ? tr('ipd', 'doctor_progress_note') : tr('ipd', 'nursing_note')}
      description={kind === 'progress' ? tr('ipd', 'soap_format') : tr('ipd', 'free_entry_or_an_sbar_handover')}
      eyebrow={tr('ipd', 'notes')}
      icon={NotebookPen}
      backTo={returnTo}
      dirty={dirty}
      onSubmit={(event) => {
        event.preventDefault();
        if (ready) save.mutate();
      }}
    >
      {kind === 'progress' ? (
        <FormSection title={tr('ipd', 'doctor_progress_note')} icon={NotebookPen}>
          <Field label={tr('ipd', 'subjective')} htmlFor="note-subjective" className="sm:col-span-2">
            <Textarea id="note-subjective" rows={2} value={subjective} onChange={(e) => setSubjective(e.target.value)} />
          </Field>
          <Field label={tr('ipd', 'objective')} htmlFor="note-objective" className="sm:col-span-2">
            <Textarea id="note-objective" rows={2} value={objective} onChange={(e) => setObjective(e.target.value)} />
          </Field>
          <Field label={tr('ipd', 'assessment')} htmlFor="note-assessment">
            <Textarea id="note-assessment" rows={2} value={assessment} onChange={(e) => setAssessment(e.target.value)} />
          </Field>
          <Field label={tr('ipd', 'plan')} htmlFor="note-plan">
            <Textarea id="note-plan" rows={2} value={plan} onChange={(e) => setPlan(e.target.value)} />
          </Field>
        </FormSection>
      ) : (
        <FormSection title={tr('ipd', 'nursing_note')} icon={NotebookPen}>
          <Field label={tr('ipd', 'type')} htmlFor="note-kind">
            <ChoiceButtons
              id="note-kind"
              layout="chips"
              value={noteKind}
              onChange={setNoteKind}
              options={(['progress', 'handover', 'care_plan', 'incident'] as const).map((k) => ({ value: k, label: humanize(k) }))}
            />
          </Field>
          <Field label={tr('ipd', 'shift')} htmlFor="note-shift">
            <ChoiceButtons
              id="note-shift"
              value={shift}
              onChange={setShift}
              onClear={() => setShift('')}
              options={(['morning', 'evening', 'night'] as const).map((s) => ({ value: s, label: humanize(s) }))}
            />
          </Field>
          {noteKind === 'handover' ? (
            <>
              <Field label={tr('ipd', 'situation')} htmlFor="note-situation" className="sm:col-span-2">
                <Textarea id="note-situation" rows={2} value={situation} onChange={(e) => setSituation(e.target.value)} />
              </Field>
              <Field label={tr('ipd', 'recommendation')} htmlFor="note-recommendation" className="sm:col-span-2">
                <Textarea id="note-recommendation" rows={2} value={recommendation} onChange={(e) => setRecommendation(e.target.value)} />
              </Field>
            </>
          ) : (
            <Field label={tr('ipd', 'note')} htmlFor="note-body" className="sm:col-span-2">
              <Textarea id="note-body" rows={4} value={body} onChange={(e) => setBody(e.target.value)} />
            </Field>
          )}
        </FormSection>
      )}

      <FormActions submitLabel={tr('ipd', 'save_note')} loading={save.isPending} incomplete={!ready} cancelTo={returnTo} />
    </FormScreen>
  );
}
